// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { RefObject, useEffect, useRef, useState } from 'react';

interface UseFocusHighlightOptions {
  // Becomes true when this element is the deep-link target. The hook only
  // reacts to the rising edge — going from false → true triggers the
  // scroll/highlight; staying true does not retrigger it.
  isFocused: boolean;
  // How long the highlight stays visible before fading. Defaults to 2400ms,
  // long enough to spot the target without feeling stuck.
  durationMs?: number;
  // Called once the highlight finishes. Typical use is for the parent to
  // drop the deep-link query param from the URL so a refresh does not
  // re-fire the effect.
  onComplete?: () => void;
}

interface UseFocusHighlightResult<T extends HTMLElement> {
  // Attach to the element you want scrolled into view + flashed.
  ref: RefObject<T>;
  // True while the highlight flash is active. Bind it to a background-color
  // sx prop with a transition for a smooth fade.
  isHighlighted: boolean;
}

const DEFAULT_DURATION_MS = 2400;

/**
 * Shared deep-link "scroll into view + flash" behavior. Used by anything that
 * needs to draw the user's eye to a specific row/section after a navigation,
 * for example "view related billing plan" jumping into the project drawer.
 *
 * Behavior:
 *   - When `isFocused` transitions to true, the referenced element is scrolled
 *     into view (smooth, centered) only if it is currently outside the
 *     viewport. Already-visible elements are left alone so the page does not
 *     jump unnecessarily.
 *   - The hook then sets `isHighlighted = true` for `durationMs` and calls
 *     `onComplete()` once the timer fires.
 *   - Unmounting or `isFocused` flipping back to false during the highlight
 *     cancels the timer cleanly.
 */
export function useFocusHighlight<T extends HTMLElement>(
  options: UseFocusHighlightOptions
): UseFocusHighlightResult<T> {
  const ref = useRef<T>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const { isFocused, onComplete } = options;
  const durationMs = options.durationMs ?? DEFAULT_DURATION_MS;

  // Keep the latest onComplete in a ref so the effect below doesn't re-run (and reset the highlight
  // timer) each time the caller passes a new inline callback — otherwise the highlight never fades.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    const node = ref.current;
    if (node === null) {
      return;
    }
    const rect = node.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const isOutOfView = rect.top < 0 || rect.bottom > viewportHeight;
    if (isOutOfView) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setIsHighlighted(true);
    const timer = window.setTimeout(() => {
      setIsHighlighted(false);
      onCompleteRef.current?.();
    }, durationMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isFocused, durationMs]);

  return { ref, isHighlighted };
}
